import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, Linkedin, Twitter, Github, Mail, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { useAuthStore } from '../store/authStore';
import { SubrivaLogo } from './SubrivaLogo';

const navLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/support', label: 'Support' },
  { to: '/contact', label: 'Contact' },
];

// Exported so auth pages (Login, Register, Forgot/Reset) can reuse the same
// marketing nav at the top — keeps a single source of truth for branding,
// links, and the "Sign in / Get Started" CTAs.
export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/75 border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group min-w-0">
          <SubrivaLogo size={36} />
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-slate-900 text-[15px]" style={{ fontFamily: 'Manrope' }}>Subriva</span>
            <span className="text-[10px] text-slate-500 -mt-0.5">HR &amp; Payroll</span>
          </div>
        </Link>

        {/* Breakpoint is `lg`, not `md`: at 768–1023px the five links plus the
            two CTAs don't fit beside the wordmark and the row starts clipping,
            so portrait tablets get the drawer like phones do. */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'text-indigo-700 bg-indigo-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <Link to="/dashboard">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                <LayoutDashboard size={14} /> Open dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900">Sign in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                  Get Started <ArrowRight size={14} />
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden w-10 h-10 flex-shrink-0 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-700"
          onClick={() => setOpen(v => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        // Capped to the space left below the 4rem bar and scrollable: on a
        // landscape phone the panel is taller than the viewport, and without
        // this the "Get Started" button at the bottom is unreachable.
        <div className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100dvh-4rem)] overflow-y-auto">
          <div className="px-4 sm:px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex flex-col gap-1">
            {navLinks.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive ? 'text-indigo-700 bg-indigo-50' : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-3 border-t border-slate-200 mt-2">
              {user ? (
                <Link to="/dashboard" className="flex-1">
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                    <LayoutDashboard size={14} /> Open dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="flex-1">
                    <Button variant="outline" className="w-full">Sign in</Button>
                  </Link>
                  <Link to="/register" className="flex-1">
                    <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 mt-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <SubrivaLogo size={40} />
              <div className="flex flex-col leading-tight">
                <span className="font-bold text-white text-base" style={{ fontFamily: 'Manrope' }}>Subriva</span>
                <span className="text-[11px] text-slate-400">HR &amp; Payroll</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-sm">
              Modern, India-compliant HR &amp; Payroll software trusted by growing businesses. Built with love by the team at Subriva Tech.
            </p>
            <div className="mt-5 flex items-center gap-2">
              {[Linkedin, Twitter, Github, Mail].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-md bg-slate-800/70 hover:bg-indigo-600 transition-colors flex items-center justify-center text-slate-300 hover:text-white">
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold mb-4">Product</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/features" className="hover:text-white">Features</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              <li><Link to="/register" className="hover:text-white">Start free trial</Link></li>
              <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><a href="#" className="hover:text-white">About Subriva</a></li>
              <li><Link to="/support" className="hover:text-white">Support</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact us</Link></li>
              <li><a href="#" className="hover:text-white">Careers</a></li>
              <li><a href="#" className="hover:text-white">Blog</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold mb-4">Legal</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              <li><Link to="/security" className="hover:text-white">Security</Link></li>
              <li><Link to="/privacy#rights" className="hover:text-white">Your data rights</Link></li>
              <li><Link to="/data-deletion" className="hover:text-white">Delete my data</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Subriva Technologies. All rights reserved.</p>
          <p className="text-xs text-slate-500">Crafted in India.</p>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Navbar />
      <Outlet />
      <Footer />
    </div>
  );
}
